import React, {
  FunctionComponent,
  useContext,
  useState,
  useEffect,
} from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import {
  Alert,
  Close,
  HelpDrawerToggle,
  HelpDrawer,
} from "@cmsgov/ds-medicare-gov";
import {
  Plan,
  DrugCost,
  DrugCosts,
  PharmacyType,
  PrescriptionDrug,
  DrugCostInfo,
  DrugFootnote,
  DrugCostCoverageReason,
  PlanDetailsSection,
} from "../../@types";
import { AppStore } from "../../app/store";
import { displayDrug } from "../../helpers/prescriptionHelpers";
import {
  useTranslate,
  isMaPlanType,
  planDataUtils,
  ifNumber,
  $$,
  formatDate,
  sendTealiumEvent,
  shouldSuppressDrugPricing,
} from "../../helpers";
import EstimatedMonthlyCosts from "./EstimatedMonthlyCosts";
import { CheckmarkIcon, ScrollTable } from "../../components";
import { FormattedHTMLMessage, FormattedMessage } from "react-intl-phraseapp";

interface DrugCostsTablesProps {
  plan: Plan;
  drugCostInfo: DrugCostInfo;
}

interface DrugCostFootnote {
  ndcs: string[];
  text: string;
  type: DrugFootnote;
}

const DrugCostsTables: FunctionComponent<DrugCostsTablesProps> = props => {
  const t = useTranslate();
  const { state } = useContext(AppStore.AppContext);
  const { mctCurrentPlanYear } = useFlags();
  const [globalFootnotes, setGlobalFootnotes] = useState<DrugCostFootnote[]>(
    []
  );
  const [pharmacyFootnotes, setPharmacyFootnotes] = useState<{
    [npi: string]: DrugCostFootnote[];
  }>({});
  const [showCoveragePhasesHelp, setShowCoveragePhasesHelp] = useState<boolean>(
    false
  );
  const isDrugSuppression = shouldSuppressDrugPricing(props.plan);

  const checkForSuppression = (val: number | string): string => {
    if (isDrugSuppression) {
      return t("na");
    } else {
      return ifNumber(val);
    }
  };

  const getPharmacyName = (npi: string): string => {
    if (npi === "") {
      return t("plan_search_prefs.mail_order_pharmacy");
    }

    const pharmacy = state.pharmacies.find(p => p.npi === npi);

    return pharmacy ? pharmacy.name : "N/A";
  };

  const sumBy = (arr: DrugCost[], prop: string): string | number => {
    return arr.reduce((prev, curr) => {
      if (isNaN(curr[prop])) {
        return "N/A";
      }
      return prev + curr[prop];
    }, 0);
  };

  const drugDeductible = planDataUtils.getAnnualDrugDeductible(
    state,
    mctCurrentPlanYear,
    props.plan
  );

  const hasDeductible = drugDeductible !== "$0.00";

  const { contract_year: year } = props.plan;

  const getDescriptionByYear = (
    descriptionKey: string,
    year: string
  ): string => {
    return t(`${descriptionKey}.${year}`);
  };

  const OON_FOOTNOTE_SUPPRESSION_LIST: DrugFootnote[] = [
    DrugFootnote.EXCLUDED_DRUG,
    DrugFootnote.PARTIAL_GAP,
    DrugFootnote.FREE_FIRST_FILL,
  ];

  const filterOutDisallowedFootnotes = (
    pharmacy: DrugCosts,
    globalFootnotes: DrugCostFootnote[]
  ): DrugCostFootnote[] =>
    globalFootnotes.filter(footnote => {
      if (
        !pharmacy.in_network &&
        OON_FOOTNOTE_SUPPRESSION_LIST.includes(footnote.type)
      ) {
        return false;
      }
      return true;
    });

  const showFootnoteSuperscripts = (
    pharmacy: DrugCosts,
    ndc: string
  ): JSX.Element => {
    const superscripts = [
      ...filterOutDisallowedFootnotes(pharmacy, globalFootnotes),
      ...(pharmacyFootnotes[pharmacy.npi] ?? []),
    ]
      .map((f, i) => (f.ndcs.includes(ndc) ? i + 1 : ""))
      .filter(f => !!f);

    return <sup className="ds-u-color--gray">{superscripts.join(",")}</sup>;
  };

  useEffect(() => {
    const { restrictions, excluded_drugs, costs } = props.drugCostInfo;

    const pharmacyNotes: { [npi: string]: DrugCostFootnote[] } = {};

    const excluded = excluded_drugs;
    const freeFills = restrictions.filter(d => d.free_first_fill);
    const partialGaps = restrictions.filter(d => d.partial_gap);

    costs.forEach(cost => {
      const { npi, drug_costs } = cost;

      const notInFormularyNdcs: string[] = [];
      const defaultPriceUsedNdcs: string[] = [];
      const frequencyNotCoveredNdcs: string[] = [];

      drug_costs.forEach(drugCost => {
        const { ndc, coverage_reason, default_price_used, covered } = drugCost;

        if (coverage_reason === DrugCostCoverageReason.NOT_IN_FORMULARY) {
          notInFormularyNdcs.push(ndc);
        } else if (
          coverage_reason === DrugCostCoverageReason.COVERED &&
          default_price_used
        ) {
          defaultPriceUsedNdcs.push(ndc);
        } else if (
          coverage_reason === DrugCostCoverageReason.FREQUENCY_NOT_COVERED &&
          !covered &&
          default_price_used
        ) {
          frequencyNotCoveredNdcs.push(ndc);
        }
      });

      pharmacyNotes[npi] = [];

      if (notInFormularyNdcs.length) {
        pharmacyNotes[npi].push({
          ndcs: notInFormularyNdcs,
          text: t("drug_footnote.not_in_formulary"),
          type: DrugFootnote.NOT_IN_FORMULARY,
        });
      }

      if (defaultPriceUsedNdcs.length) {
        pharmacyNotes[npi].push({
          ndcs: defaultPriceUsedNdcs,
          text: t("drug_footnote.default_price_used"),
          type: DrugFootnote.DEFAULT_PRICE,
        });
      }

      if (frequencyNotCoveredNdcs.length) {
        pharmacyNotes[npi].push({
          ndcs: frequencyNotCoveredNdcs,
          text: t("drug_footnote.frequency_not_covered"),
          type: DrugFootnote.FREQUENCY_NOT_COVERED,
        });
      }
    });

    const notes: DrugCostFootnote[] = [];

    if (excluded.length) {
      notes.push({
        ndcs: excluded,
        text: t("excluded_drugs_footnote"),
        type: DrugFootnote.EXCLUDED_DRUG,
      });
    }

    if (freeFills.length) {
      notes.push({
        ndcs: freeFills.map(x => x.ndc),
        text: t("drug_footnote.free_first_fill"),
        type: DrugFootnote.FREE_FIRST_FILL,
      });
    }

    if (partialGaps.length) {
      notes.push({
        ndcs: partialGaps.map(x => x.ndc),
        text: t("drug_footnote.partial_gap"),
        type: DrugFootnote.PARTIAL_GAP,
      });
    }

    setGlobalFootnotes(notes);
    setPharmacyFootnotes(pharmacyNotes);
  }, [props.drugCostInfo]);

  return (
    <div className="DrugCostsTables ds-u-margin-bottom--7" id="drug-costs">
      {state.prescriptions.length > 0 &&
        state.pharmacyType !== PharmacyType.NONE && (
          <>
            <div className="ds-u-fill--primary-alt-lightest ds-u-display--flex m-u-flex-direction--column">
              <h2
                className="PlanDetailList__header"
                id={PlanDetailsSection.ESTIMATED_DRUG_COSTS}
                tabIndex={-1}
              >
                {t("plan_details.drug_costs.estimated_coverage")}
              </h2>
              <span className="ds-u-margin-x--2">
                {t("plan_details.drug_costs.estimated_coverage_info")}
              </span>
              <div className="ds-u-margin--2">
                <HelpDrawerToggle
                  helpDrawerOpen={showCoveragePhasesHelp}
                  showDrawer={(): void => {
                    setShowCoveragePhasesHelp(true);
                    sendTealiumEvent(
                      "Help Drawer",
                      "Learn More about Coverage Phases"
                    );
                  }}
                >
                  {t("plan_terms.learn_more_about_coverage_phases")}
                </HelpDrawerToggle>
              </div>
            </div>
            {showCoveragePhasesHelp && (
              <div className="mct-c-help-drawer">
                <HelpDrawer
                  title={t("plan_terms.learn_more_about_coverage_phases")}
                  onCloseClick={(): void => setShowCoveragePhasesHelp(false)}
                  closeButtonText={<Close />}
                  ariaLabel={t("plan_terms.close_coverage_phases_help_drawer")}
                >
                  {t("plan_terms.coverage_phases_help.overview")}
                  <dl>
                    <p>
                      <dt className="ds-u-display--inline ds-u-font-weight--bold">
                        {t("plan_terms.coverage_phases_help.initial_coverage")}
                      </dt>
                      <span> - </span>
                      <dd className="ds-u-margin--0 ds-u-display--inline">
                        {getDescriptionByYear(
                          "plan_terms.coverage_phases_help.initial_coverage_description",
                          year
                        )}
                      </dd>
                    </p>

                    <p>
                      <dt className="ds-u-display--inline ds-u-font-weight--bold">
                        {t("plan_terms.coverage_phases_help.coverage_gap")}{" "}
                        {t(
                          "plan_terms.coverage_phases_help.coverage_gap_aside"
                        )}
                      </dt>
                      <span> - </span>
                      <dd className="ds-u-margin--0 ds-u-display--inline">
                        {getDescriptionByYear(
                          "plan_terms.coverage_phases_help.coverage_gap_description",
                          year
                        )}
                      </dd>
                    </p>

                    <p>
                      <dt className="ds-u-display--inline ds-u-font-weight--bold">
                        {t(
                          "plan_terms.coverage_phases_help.catastrophic_coverage"
                        )}{" "}
                        {t(
                          "plan_terms.coverage_phases_help.catastrophic_coverage_aside"
                        )}
                      </dt>
                      <span> - </span>
                      <dd className="ds-u-margin--0 ds-u-display--inline">
                        {getDescriptionByYear(
                          "plan_terms.coverage_phases_help.catastrophic_coverage_description",
                          year
                        )}
                      </dd>
                    </p>
                  </dl>
                </HelpDrawer>
              </div>
            )}

            {props.drugCostInfo.costs.map(pharmacy => {
              if (
                pharmacy.mail_order &&
                ![PharmacyType.MAIL, PharmacyType.MAIL_AND_RETAIL].includes(
                  state.pharmacyType
                )
              ) {
                return null;
              }

              const { phase_information: phaseInfo } = pharmacy;

              const showHeading =
                !!phaseInfo.initial_coverage_start ||
                (!phaseInfo.initial_coverage_start && hasDeductible);

              return (
                <div
                  key={pharmacy.npi}
                  className="DrugCostsTables__tableContainer"
                >
                  <h2 className="ds-h2 ds-u-margin--0">
                    {getPharmacyName(pharmacy.npi)} -{" "}
                    {t("plan_details.drug_costs.coverage_levels")}
                  </h2>
                  {!pharmacy.mail_order && (
                    <>
                      <p className="ds-u-margin--0">
                        <CheckmarkIcon checked={pharmacy.in_network} />
                        {` `}
                        {pharmacy.preferred
                          ? t("pharmacy.terms.preferred_network")
                          : pharmacy.in_network
                          ? t("pharmacy.terms.standard_in_network")
                          : t("pharmacy.terms.out_of_network")}
                        {` `}pharmacy
                      </p>
                      {!pharmacy.in_network && (
                        <div className="ds-u-margin-top--1">
                          <Alert hideIcon={true}>
                            {isMaPlanType(props.plan.plan_type)
                              ? t(
                                  "plan_details.drug_costs.out_of_network_warning_ma"
                                )
                              : t(
                                  "plan_details.drug_costs.out_of_network_warning"
                                )}
                          </Alert>
                        </div>
                      )}
                    </>
                  )}

                  <ScrollTable className="ds-u-margin-top--3">
                    <thead>
                      <tr>
                        <th>{t("plan_details.drug_costs.selected_drugs")}</th>
                        <th>{t("plan_details.drug_costs.retail_cost")}</th>
                        {hasDeductible && (
                          <th>
                            {t("plan_details.drug_costs.before_deductible")}
                          </th>
                        )}
                        <th>{t("plan_details.drug_costs.after_deductible")}</th>
                        <th>
                          {t("plan_details.drug_costs.cost_in_coverage_gap")}
                        </th>
                        <th>
                          {t("plan_details.drug_costs.after_coverage_gap")}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {pharmacy.drug_costs.map(cost => {
                        return (
                          <tr key={cost.ndc}>
                            <td>
                              {displayDrug(
                                t,
                                state.prescriptions.find(
                                  p => p.ndc === cost.ndc
                                ) || ({} as PrescriptionDrug)
                              )}{" "}
                              {showFootnoteSuperscripts(pharmacy, cost.ndc)}
                            </td>
                            <td>{checkForSuppression(cost.full_cost)}</td>
                            {hasDeductible && (
                              <td>
                                {checkForSuppression(cost.deductible_cost)}
                              </td>
                            )}
                            <td>{checkForSuppression(cost.initial_cost)}</td>
                            <td>{checkForSuppression(cost.gap_cost)}</td>
                            <td>
                              {checkForSuppression(cost.catastrophic_cost)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>{t("plan_details.drug_costs.monthly_totals")}</td>
                        <td>
                          {checkForSuppression(
                            sumBy(pharmacy.drug_costs, "full_cost")
                          )}
                        </td>
                        {hasDeductible && (
                          <td>
                            {checkForSuppression(
                              sumBy(pharmacy.drug_costs, "deductible_cost")
                            )}
                          </td>
                        )}
                        <td>
                          {checkForSuppression(
                            sumBy(pharmacy.drug_costs, "initial_cost")
                          )}
                        </td>
                        <td>
                          {checkForSuppression(
                            sumBy(pharmacy.drug_costs, "gap_cost")
                          )}
                        </td>
                        <td>
                          {checkForSuppression(
                            sumBy(pharmacy.drug_costs, "catastrophic_cost")
                          )}
                        </td>
                      </tr>
                      <tr className="ds-u-fill--white">
                        <td colSpan={hasDeductible ? 6 : 5}>
                          <ul
                            id={`pharmacy-${pharmacy.npi}-cost-footnotes`}
                            className="ds-c-list ds-c-list--bare ds-u-color--gray pharmacy-cost-footnotes"
                          >
                            {[
                              ...filterOutDisallowedFootnotes(
                                pharmacy,
                                globalFootnotes
                              ),
                              ...(pharmacyFootnotes[pharmacy.npi] ?? []),
                            ].map((footnote, i) => (
                              <li key={i}>
                                <sup>{i + 1} </sup>
                                <span>{footnote.text}</span>
                              </li>
                            ))}
                          </ul>
                        </td>
                      </tr>
                    </tfoot>
                  </ScrollTable>

                  {isDrugSuppression ? (
                    <p>{t("plan_terms.drug_pricing_not_available")}</p>
                  ) : (
                    <>
                      <h3 className="ds-h3 ds-u-margin-top--3">
                        {t("plan_terms.estimated_yearly_total_cost")}
                      </h3>
                      <p className="ds-u-margin-top--1 ds-u-margin-bottom--0">
                        <FormattedHTMLMessage
                          id="plan_details.drug_costs_and_premium.pay_per_year"
                          values={{
                            total: $$(
                              props.plan.total_remaining_premium +
                                pharmacy.estimated_yearly_total
                            ),
                          }}
                        />
                      </p>

                      {showHeading && (
                        <p className="ds-u-margin--0">
                          {t(
                            "plan_details.drug_costs.based_on_costs_estimated_that"
                          )}
                        </p>
                      )}
                      <ul className="ds-u-margin-top--0">
                        {phaseInfo.initial_coverage_start ? (
                          <>
                            {hasDeductible && (
                              <li data-testid="meet-deductible">
                                <FormattedMessage
                                  id="plan_details.drug_costs.meet_deductible"
                                  values={{
                                    amount: (
                                      <b>
                                        <FormattedMessage
                                          id="plan_details.drug_costs.amount_deductible"
                                          values={{
                                            amount: $$(
                                              phaseInfo.deductible_amount
                                            ),
                                          }}
                                        />
                                      </b>
                                    ),
                                    month: formatDate(
                                      phaseInfo.initial_coverage_start,
                                      "MMMM"
                                    ),
                                  }}
                                />
                              </li>
                            )}
                            {phaseInfo.gap_start ? (
                              <>
                                <li data-testid="enter-coverage-gap">
                                  <FormattedHTMLMessage
                                    id="plan_details.drug_costs.enter_phase_in_month"
                                    values={{
                                      month: formatDate(
                                        phaseInfo.gap_start,
                                        "MMMM"
                                      ),
                                    }}
                                  />
                                </li>
                                {phaseInfo.catastrophic_start ? (
                                  <li data-testid="exit-coverage-gap">
                                    <FormattedHTMLMessage
                                      id="plan_details.drug_costs.exit_phase_in_month"
                                      values={{
                                        month: formatDate(
                                          phaseInfo.catastrophic_start,
                                          "MMMM"
                                        ),
                                      }}
                                    />
                                  </li>
                                ) : (
                                  <li data-testid="wont-exit-coverage-gap">
                                    <FormattedHTMLMessage id="plan_details.drug_costs.not_exit_phase" />
                                  </li>
                                )}
                              </>
                            ) : (
                              <li data-testid="wont-enter-coverage-gap">
                                <FormattedHTMLMessage id="plan_details.drug_costs.not_enter_phase_this_year" />
                              </li>
                            )}
                          </>
                        ) : (
                          <>
                            {hasDeductible && (
                              <li data-testid="wont-meet-deductible">
                                <FormattedMessage
                                  id="plan_details.drug_costs.not_meet_deductible"
                                  values={{
                                    amount: (
                                      <b>
                                        <FormattedMessage
                                          id="plan_details.drug_costs.amount_deductible"
                                          values={{
                                            amount: $$(
                                              phaseInfo.deductible_amount
                                            ),
                                          }}
                                        />
                                      </b>
                                    ),
                                  }}
                                />
                              </li>
                            )}
                          </>
                        )}
                      </ul>

                      <EstimatedMonthlyCosts
                        monthlyCosts={pharmacy.estimated_monthly_costs}
                        monthlyPremium={props.plan.calculated_monthly_premium}
                      />
                    </>
                  )}
                </div>
              );
            })}
          </>
        )}
    </div>
  );
};

export default DrugCostsTables;
